import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { func, object, string } from "prop-types";
import { useIntl } from "react-intl";
import {
  TextInput,
  SelectMenu,
  ButtonTW,
  FormLabel,
  useToast
} from "@bafsllc/ui-shared";
import { ConcatName } from "../../../../../services/Entities";
import {
  TASK_PRIORITY_HASH,
  TASK_STATUS_HASH
} from "../../../../../constants/taskManagement";
import {
  getClientDepartment,
  getClientDepartments,
  getTask,
  getTaskEntitiesByInstitutionUuid,
  getTaskTypes,
  setTaskSidesheetEditMode
} from "../../../../../actions/Tasks";
import BafsLogo from "../../../../../assets/institutions/logo.png";
import "../../index.css";
export function Form({ data, className, addTask, updateTask }) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { addToast } = useToast();
  //Redux
  const auth = useSelector(state => state.auth);
  const LosPhasesReducer = useSelector(state => state.LosPhasesReducer);
  const TaskMgmt = useSelector(state => state.TaskMgmt);
  // Auth
  const createdByUuid = auth?.userUuid || null;
  const institutionUuid = auth?.institutionUuid || null;
  // Tasks
  const task = TaskMgmt?.task || null;
  const taskTypes = TaskMgmt?.taskTypes || null;
  const taskEntities = TaskMgmt?.taskEntities || null;
  const taskUsers = TaskMgmt?.taskUsers || null;
  const clientDepartments = TaskMgmt?.clientDepartments || null;
  // Loan App
  const loanAppId = LosPhasesReducer?.loanApp?.app_id || null;
  const loanAppInstitutionUuid =
    LosPhasesReducer?.loanApp?.institution_uuid || null;
  // State
  const [loading, setLoading] = useState(false);
  // Form State
  const [taskTypeName, setTaskTypeName] = useState("");
  const [title, setTitle] = useState(data?.title || "");
  const [taskTypeUuid, setTaskTypeUuid] = useState(data?.task_type_uuid || "");
  const [clientDepartmentUuid, setClientDepartmentUuid] = useState(
    data?.assigned_department_uuid || ""
  );
  const [assignedTo, setAssignedTo] = useState(data?.assigned_to || "");
  const [status, setStatus] = useState(data?.status || "");
  const [holdReason, setHoldReason] = useState("");
  const [description, setDescription] = useState(data?.description || "");
  const [accountNumber] = useState(
    data?.loan_uuid || data?.loan_app_uuid || loanAppId || ""
  );
  const [entityUuid, setEntityUuid] = useState(
    data?.entity_uuid || LosPhasesReducer?.entityDetails?.uuid
  );
  const [priority, setPriority] = useState(data?.priority || "standard");

  const createdBy = useMemo(() => {
    let name = `${auth?.firstName || ""} ${auth?.lastName || ""}`;

    return name.trim();
  }, [auth?.firstName, auth?.lastName]);

  const handleCloseSideSheet = () => {
    dispatch({
      type: "RESET_TASK_SIDESHEET"
    });
  };

  const onCancel = () => {
    if (task?.uuid) {
      dispatch({
        type: "SET_TASK_SIDESHEET_EDIT_MODE",
        sidesheetEditMode: false
      });
      return;
    }

    handleCloseSideSheet();
  };

  const creatingFromLOS = useMemo(() => {
    return Boolean(LosPhasesReducer?.loanApp?.app_id);
  }, [LosPhasesReducer?.loanApp?.app_id]);

  const clientDepartment = useMemo(() => {
    if (
      clientDepartmentUuid &&
      Array.isArray(clientDepartments) &&
      clientDepartments.length > 0
    ) {
      return clientDepartments.find(te => te.uuid === clientDepartmentUuid);
    }

    return null;
  }, [clientDepartmentUuid, clientDepartments]);

  const accountNumberLabel = useMemo(() => {
    if (data?.loan_uuid) {
      return "Loan ID";
    }

    // NOTE: May need in future.
    // if (data?.loan_app_uuid) {
    //   return "Loan App ID";
    // }

    return "Loan App ID";
  }, [data?.loan_uuid]);

  const excludeOnHoldOption = useMemo(() => {
    if (
      !task?.sla_status ||
      (task?.sla_status && task?.sla_status !== "within")
    ) {
      return true;
    }

    return false;
  }, [task?.sla_status]);

  useEffect(() => {
    if (taskTypeUuid && Array.isArray(taskTypes) && taskTypes.length > 0) {
      const taskTemplate = taskTypes?.find(tt => tt?.uuid === taskTypeUuid);

      if (taskTemplate?.task_type_name) {
        setTaskTypeName(taskTemplate?.task_type_name);
      }

      if (taskTemplate?.department_uuid) {
        dispatch(getClientDepartment(taskTemplate?.department_uuid));
        setClientDepartmentUuid(taskTemplate?.department_uuid);
      }
    }
  }, [dispatch, task?.uuid, task?.department_uuid, taskTypes, taskTypeUuid]);

  const onSubmit = async e => {
    e.preventDefault();

    setLoading(true);

    const dataToSave = {
      entity_uuid: entityUuid || null,
      // asset_uuid: null,
      requesting_institution_uuid: loanAppInstitutionUuid || institutionUuid,
      task_type_uuid: taskTypeUuid,
      description,
      assigned_to: assignedTo,
      priority
    };

    if (task?.uuid) {
      // UPDATE

      if (status === "on_hold" && task?.status !== "on_hold") {
        dataToSave.holdReason = holdReason;
      }

      await updateTask({
        ...dataToSave,
        title,
        entity_uuid: entityUuid || null,
        uuid: task?.uuid,
        status
      });

      dispatch(getTask(task?.uuid));
      dispatch(setTaskSidesheetEditMode({ sidesheetEditMode: false }));
      setLoading(false);
    } else {
      // CREATE
      let standardTitle = taskTypeName;

      if (creatingFromLOS) {
        standardTitle += ` : ${LosPhasesReducer?.loanApp?.app_id}`;

        dataToSave.loan_app_uuid = LosPhasesReducer?.loanApp?.uuid;
      }

      await addTask({
        ...dataToSave,
        title: standardTitle,
        entity_uuid: LosPhasesReducer?.entityDetails?.uuid,
        created_by_uuid: createdByUuid,
        created_by: createdBy,
        source: "BLAST",
        status: assignedTo ? "in_progress" : "unassigned"
      });

      if (creatingFromLOS) {
        addToast({
          id: "MESSAGE_SENT",
          title: {
            id: "BAFS_SERVICES_REQUEST_SENT_SUCCESS_TITLE",
            defaultMessage: "Success"
          },
          message: {
            id: "BAFS_SERVICES_REQUEST_SENT_SUCCESS_MESSAGE",
            defaultMessage: "Your message was sent."
          },
          variant: "success"
        });
      }

      dispatch({
        type: "RESET_TASK_SIDESHEET"
      });
    }
  };

  useEffect(() => {
    if (creatingFromLOS) {
      dispatch(getTaskTypes());
      dispatch(getClientDepartments());
      dispatch(
        getTaskEntitiesByInstitutionUuid(
          loanAppInstitutionUuid || institutionUuid
        )
      );
    }
  }, [creatingFromLOS, dispatch, institutionUuid, loanAppInstitutionUuid]);

  return (
    <div className={className}>
      <form onSubmit={onSubmit}>
        <div className={`mb-3 ${!task?.uuid ? "hidden" : ""}`}>
          <TextInput
            handleInputChange={e => {
              const { value } = e.currentTarget;
              setTitle(value);
            }}
            autoFocus
            labelText="Task Name"
            name="title"
            value={
              task?.uuid
                ? title
                : `${taskTypeName} : ${LosPhasesReducer?.loanApp?.app_id}`
            }
            required
            testId="manageTask-title"
          />
        </div>

        <div className="mb-3">
          <FormLabel
            labelText={formatMessage({ id: "DESCRIPTION" })}
            required
          />

          <textarea
            type="text"
            name="description"
            value={description}
            required
            onChange={e => {
              const { value } = e.target;
              setDescription(value);
            }}
          />
        </div>

        <div className="grid grid-cols-2 gap-6 mb-3">
          <div>
            <SelectMenu
              labelText="Task Template"
              inputId="task-template-field"
              className="six wide field" // nine || six
              name="taskTypeUuid"
              dataArray={taskTypes}
              dataLabelKey="task_type_name"
              dataValueKey="uuid"
              value={taskTypeUuid || ""}
              required
              onChange={args => {
                setTaskTypeUuid(args?.value || "");
              }}
            />
          </div>
          <div>
            <SelectMenu
              labelText="Entity"
              inputId="entity-field"
              className="six wide field" // nine || six
              name="entityUuid"
              dataArray={taskEntities}
              dataLabelKey={item => ConcatName(item)}
              dataValueKey="uuid"
              value={entityUuid || ""}
              onChange={args => {
                setEntityUuid(args?.value || "");
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-3">
          <div className={!clientDepartment?.uuid && "opacity-50"}>
            <FormLabel labelText="Client Department" required />

            <FakeInput
              className={`min-h-[42px] ${
                clientDepartment?.uuid && "bg-neutral-50"
              }`}
            >
              {clientDepartment?.name || " "}
            </FakeInput>
          </div>
          <div>
            <FormLabel labelText={accountNumberLabel} />

            <FakeInput className="text-neutral-500 bg-neutral-50">
              {accountNumber || "n/a"}
            </FakeInput>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-3">
          <div>
            {creatingFromLOS ? (
              <>
                <FormLabel
                  labelText={formatMessage({ id: "ASSIGN_TO" })}
                  required
                />
                <div className="border border-neutral-300 bg-neutral-100 p-2 flex items-center mb-2 rounded">
                  <img
                    src={BafsLogo}
                    className="logo h-[20px]"
                    alt="Business Alliance Financial Services"
                  />
                  <span className="ml-2 text-sm">
                    Business Alliance Financial Services
                  </span>
                </div>
              </>
            ) : (
              <SelectMenu
                labelText={formatMessage({ id: "ASSIGN_TO" })}
                inputId="assignedTo-field"
                className="six wide field" // nine || six
                name="assignedTo"
                dataArray={taskUsers}
                dataLabelKey={ConcatName}
                dataValueKey="uuid"
                value={assignedTo || ""}
                onChange={args => {
                  setAssignedTo(args?.value || "");
                }}
              />
            )}
          </div>
          <div>
            <SelectMenu
              labelText={formatMessage({ id: "PRIORITY" })}
              inputId="status-field"
              className="six wide field" // nine || six
              name="priority"
              dataMap={TASK_PRIORITY_HASH}
              value={priority || ""}
              required
              onChange={args => {
                setPriority(args?.value || "");
              }}
            />
          </div>
        </div>

        {task?.uuid && (
          <div className="grid grid-cols-2 gap-6 mb-3">
            <div>
              <SelectMenu
                labelText={formatMessage({ id: "STATUS" })}
                inputId="status-field"
                className="six wide field" // nine || six
                name="status"
                dataMap={TASK_STATUS_HASH}
                disabledOptionsMap={{
                  ...(excludeOnHoldOption ? { on_hold: true } : {})
                }}
                value={status || ""}
                required
                onChange={args => {
                  setStatus(args?.value || "");
                }}
              />
            </div>

            {status === "on_hold" && task.status !== "on_hold" && (
              <div className="mb-3">
                <FormLabel
                  labelText={formatMessage({ id: "REASON_FOR_HOLD" })}
                  required
                />
                <textarea
                  type="text"
                  name="holdReason"
                  value={holdReason}
                  required
                  onChange={e => {
                    const { value } = e.target;
                    setHoldReason(value);
                  }}
                />
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end items-center">
          <ButtonTW
            disabled={loading}
            onClick={onCancel}
            className="mr-3"
            size="md"
            variant="tertiary"
            type="button"
          >
            {formatMessage({ id: "CANCEL" })}
          </ButtonTW>
          <ButtonTW
            disabled={loading}
            loading={loading}
            iconPosition="right"
            size="md"
            variant="primary"
            type="submit"
          >
            {data?.uuid
              ? formatMessage({ id: "SAVE_TASK" })
              : formatMessage({ id: "CREATE_TASK" })}
          </ButtonTW>
        </div>
      </form>
    </div>
  );
}

Form.propTypes = {
  data: object.isRequired,
  className: string,
  addTask: func.isRequired,
  updateTask: func.isRequired
};

export default Form;

const FakeInput = ({ children, className }) => (
  <div
    className={`${className} border border-neutral-300 rounded text-base py-2 px-3 text-nowrap overflow-hidden`}
  >
    {children}
  </div>
);
